/* Infinote PDP gallery controller.
   Takes over the Glozin <slide-with-thumbs> swipers (markup, images, lightbox stay Glozin's) and adds:
   - desktop: crossfade main image + vertical thumbnail rail; mobile: native swipe
   - "01 / 07" counter, keyboard + accessible thumbnails, reduced-motion support
   - semantic view roles read from the media alt text (", front view", ", open, ruled pages" ...), so colour switching keeps the same view
   Media ORDER is decided server-side in sections/infinote-product.liquid (same role rules as roleOf below). */
(() => {
  if (window.__infinoteGallery) return; window.__infinoteGallery = true;

  const LABELS = {
    front: 'Front cover', 'front-wrap': 'Front cover with paper wrap', back: 'Back cover', 'back-wrap': 'Back cover with paper wrap',
    planner: 'Planner pages', 'open-ruled': 'Open ruled pages', 'open-blank': 'Open blank pages', instructions: 'Instructions page', pocket: 'Inside back pocket',
    branding: 'Branding page', lifestyle: 'In use', other: 'Product image',
  };
  const roleOf = (alt) => {
    const a = (alt || '').toLowerCase(), wrap = a.includes('wrap');
    if (a.includes('planner pages')) return 'planner';
    if (a.includes('ruled')) return 'open-ruled';
    if (a.includes('blank')) return 'open-blank';
    if (a.includes('instructions')) return 'instructions';
    if (a.includes('pocket')) return 'pocket';
    if (a.includes('branding')) return 'branding';
    if (a.includes('lifestyle')) return 'lifestyle';
    if (a.includes('front')) return wrap ? 'front-wrap' : 'front';
    if (a.includes('back view')) return wrap ? 'back-wrap' : 'back';
    return 'other';
  };
  const KEY = 'inf.gallery.role';
  const desktopMQ = window.matchMedia('(min-width: 1024px)');
  const reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  const pad = (n) => String(n).padStart(2, '0');
  const store = (v) => { try { v ? sessionStorage.setItem(KEY, v) : sessionStorage.removeItem(KEY); } catch (_) {} };
  const take = () => { try { const v = sessionStorage.getItem(KEY); sessionStorage.removeItem(KEY); return v; } catch (_) { return null; } };

  const slidesOf = (el) => [...el.querySelectorAll('.media-main-swiper .swiper-slide')];
  const thumbsOf = (el) => [...el.querySelectorAll('.thumbnail-slide .swiper-slide')];
  const altOf = (slide) => (slide.querySelector('img') || {}).alt || '';
  const currentRole = (el) => { const s = slidesOf(el)[el.globalSlide ? el.globalSlide.activeIndex : 0]; return s ? s.dataset.infRole : null; };

  const ensureCounter = (el) => {
    const host = el.querySelector('.media-main-swiper');
    let c = host.querySelector('.inf-gallery__counter');
    if (!c) { c = document.createElement('div'); c.className = 'inf-gallery__counter'; c.setAttribute('aria-live', 'polite'); host.appendChild(c); }
    return c;
  };

  const update = (el) => {
    const sw = el.globalSlide; if (!sw) return;
    const total = slidesOf(el).length, i = sw.activeIndex;
    const c = ensureCounter(el); c.textContent = `${pad(i + 1)} / ${pad(total)}`;
    thumbsOf(el).forEach((t, n) => { if (n === i) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current'); });
    const thumbs = el.thumbnailSlide;
    if (thumbs && !thumbs.destroyed && desktopMQ.matches) thumbs.slideTo(Math.max(0, i - 2), reduceMQ.matches ? 0 : 200);
  };

  const tag = (el) => {
    const slides = slidesOf(el), thumbs = thumbsOf(el), total = slides.length;
    slides.forEach((s) => { s.dataset.infRole = roleOf(altOf(s)); });
    thumbs.forEach((t, n) => {
      const role = roleOf(altOf(t)); t.dataset.infRole = role;
      t.setAttribute('role', 'button'); t.setAttribute('tabindex', '0');
      t.setAttribute('aria-label', `Show image ${n + 1} of ${total}: ${LABELS[role] || LABELS.other}`);
      if (!t.dataset.infKeys) {
        t.dataset.infKeys = '1';
        t.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); el.globalSlide && el.globalSlide.slideTo(n); } });
      }
    });
    // first image is the page's LCP: load it eagerly at high priority; the rest keep the theme's lazy loading
    const first = slides[0] && slides[0].querySelector('img');
    if (first) { first.loading = 'eager'; first.setAttribute('fetchpriority', 'high'); }
  };

  const build = (el, keepIndex) => {
    const host = el.querySelector('.media-main-swiper .swiper-wrapper-preview');
    if (!host || typeof Swiper === 'undefined') return;
    const startAt = keepIndex && el.globalSlide ? el.globalSlide.activeIndex : 0;
    const desktop = desktopMQ.matches;
    try { if (el.thumbnailSlide && !el.thumbnailSlide.destroyed) el.thumbnailSlide.destroy(true, true); } catch (_) {}
    try { if (el.globalSlide && !el.globalSlide.destroyed) el.globalSlide.destroy(true, true); } catch (_) {}
    tag(el);

    const thumbEl = el.querySelector('.thumbnail-slide');
    if (thumbEl) {
      el.thumbnailSlide = new Swiper(thumbEl, {
        direction: desktop ? 'vertical' : 'horizontal', slidesPerView: 'auto', spaceBetween: 8, watchSlidesProgress: true,
        freeMode: true, mousewheel: desktop ? { forceToAxis: true, releaseOnEdges: true } : false,
      });
    }
    const nav = el.querySelector('.main-slide__navigation');
    el.globalSlide = new Swiper(host, {
      initialSlide: startAt, slidesPerView: 1, spaceBetween: 0, watchSlidesProgress: true,
      effect: desktop ? 'fade' : 'slide', fadeEffect: { crossFade: true },
      speed: reduceMQ.matches ? 0 : (desktop ? 220 : 300), simulateTouch: false,
      navigation: nav ? { nextEl: nav.querySelector('.swiper-button-next'), prevEl: nav.querySelector('.swiper-button-prev') } : false,
      a11y: { enabled: true, prevSlideMessage: 'Previous image', nextSlideMessage: 'Next image', slideLabelMessage: 'Image {{index}} of {{slidesLength}}' },
      thumbs: el.thumbnailSlide ? { swiper: el.thumbnailSlide } : undefined,
      on: { slideChange: () => update(el), init: () => update(el) },
    });
    update(el);

    const region = el.querySelector('.media-main-swiper');
    region.setAttribute('role', 'region'); region.setAttribute('aria-roledescription', 'carousel'); region.setAttribute('aria-label', 'Product images'); region.setAttribute('tabindex', '0');
    if (!el.dataset.infKeys) {
      el.dataset.infKeys = '1';
      el.addEventListener('keydown', (e) => {
        if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
        if (e.target.closest && e.target.closest('input, textarea, select')) return;
        if (e.key === 'ArrowRight') { e.preventDefault(); el.globalSlide.slideNext(); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); el.globalSlide.slidePrev(); }
        else if (e.key === 'Home') { e.preventDefault(); el.globalSlide.slideTo(0); }
        else if (e.key === 'End') { e.preventDefault(); el.globalSlide.slideTo(slidesOf(el).length - 1); }
      });
    }
  };

  const showRole = (el, role) => {
    if (!role || !el.globalSlide) return;
    const i = slidesOf(el).findIndex((s) => s.dataset.infRole === role);
    el.globalSlide.slideTo(i >= 0 ? i : 0, 0);        // missing role on this colour: fall back to the clean front (index 0)
  };

  const setup = () => {
    const el = document.querySelector('.product-detail__media slide-with-thumbs');
    if (!el || el.dataset.infGallery === '1') return;
    el.dataset.infGallery = '1';
    build(el, false);
    const wanted = take(); if (wanted && wanted !== 'front') showRole(el, wanted);
    desktopMQ.addEventListener('change', () => build(el, true));
    reduceMQ.addEventListener('change', () => build(el, true));
  };

  // remember the current view when a colour swatch is chosen; restore it after the in-page product swap
  document.addEventListener('click', (e) => {
    const a = e.target.closest && e.target.closest('a.infinote-family__swatch');
    if (!a || a.classList.contains('is-selected')) return;
    const el = document.querySelector('.product-detail__media slide-with-thumbs'); if (el) store(currentRole(el));
  }, true);
  document.addEventListener('infinote:product-switched', () => {
    requestAnimationFrame(() => { setup(); window.dispatchEvent(new Event('resize')); });   // resize re-arms the theme's lightbox on the new gallery
  });

  const start = () => customElements.whenDefined('slide-with-thumbs').then(() => requestAnimationFrame(setup));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
